import React, { useState, useEffect } from 'react';
import {
  Table,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  Typography,
  Box,
} from '@mui/material';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { updateEmployeeAvailability } from '../../../../../repository/UserDAO';
import { decimalToDayjs, dayjsToDecimal } from '../../../../../utils/time_utils/TimeUtils';
import BaseModal from '../../../../../components/modals/BaseModal';
import { useLoadingContext } from '../../../../../context/loading/LoadingContext';
import { useToast, ToastType } from '../../../../../context/toast/ToastContext';
import { useUserContext } from '../../../../../context/user_context/UserContext';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../../resources/strings/StringKeys';

const AvailabilityModal = ({ show, onHide, availability: initialAvailability, employeeId }) => {
  const { setLoadingData } = useLoadingContext();
  const { showToast } = useToast();
  const { firebaseUser } = useUserContext();
  const weekDays = [
    "SUN",
    "MON", 
    "TUE",
    "WED",
    "THU",
    "FRI",
    "SAT"
  ];
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');

  const getDayTranslationKey = (day) => {
    const dayMap = {
      'SUN': 'sunday',
      'MON': 'monday',
      'TUE': 'tuesday',
      'WED': 'wednesday',
      'THU': 'thursday',
      'FRI': 'friday',
      'SAT': 'saturday'
    };
    return dayMap[day];
  };

  const initializeAvailability = (availability) => {
    const initial = {};
    weekDays.forEach(day => {
      const dayAvailability = availability[day] || {};
      initial[day] = {
        restrictions: dayAvailability.restrictions || 'none',
        times: (dayAvailability.available_times || []).map(shift => ({
          start: decimalToDayjs(shift.from),
          end: decimalToDayjs(shift.to),
        })),
      };
    });
    return initial;
  };

  const [availability, setAvailability] = useState(initializeAvailability(initialAvailability));
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  const [restrictionDialogOpen, setRestrictionDialogOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (show) {
      setAvailability(initializeAvailability(initialAvailability));
    }
  }, [show, initialAvailability]);

  const validateAvailability = () => {
    const errors = {};
    weekDays.forEach(day => {
      availability[day].times.forEach((shift, index) => {
        if (!shift.start || !shift.end) {
          if (!errors[day]) errors[day] = {};
          errors[day][index] = t(StringKeys.TIME_IS_REQUIRED);
        }
      });
    });
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateAvailability();
    if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
    }

    setLoadingData(true);
    try {
        const formattedAvailability = {};
        weekDays.forEach(day => {
            formattedAvailability[day] = {
                restrictions: availability[day].restrictions,
                available_times: availability[day].times.map(shift => ({
                    from: dayjsToDecimal(shift.start),
                    to: dayjsToDecimal(shift.end),
                })),
            };
        });

        await updateEmployeeAvailability(
            firebaseUser.organization,
            employeeId,
            { availability: formattedAvailability },
            showToast
        );

        showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
        onHide();
    } catch (error) {
        console.error("Failed to update availability:", error);
        showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
      } finally {
        setLoadingData(false);
    }
};

  const resetForm = () => {
    setAvailability(initializeAvailability(initialAvailability));
    setValidationErrors({});
  };

  const handleTimeChange = (day, index, key, value) => {
    const updatedAvailability = { ...availability };
    updatedAvailability[day].times[index][key] = value;

    if (validationErrors[day]?.[index]) {
      const updatedErrors = { ...validationErrors };
      delete updatedErrors[day][index];
      if (Object.keys(updatedErrors[day]).length === 0) delete updatedErrors[day];
      setValidationErrors(updatedErrors);
    }

    if (key === 'start' && value.isAfter(updatedAvailability[day].times[index].end)) {
      setConflictMessage(t(StringKeys.START_TIME_CANNOT_BE_AFTER_END_TIME));
      setConflictDialogOpen(true);
      return;
    }

    if (key === 'end' && value.isBefore(updatedAvailability[day].times[index].start)) {
      setConflictMessage(t(StringKeys.END_TIME_CANNOT_BE_BEFORE_START_TIME));
      setConflictDialogOpen(true);
      return;
    }

    if (!validateTimes(day, updatedAvailability[day].times)) {
      setConflictMessage(t(StringKeys.NEW_START_TIME_CANNOT_BE_BEFORE_EXISTING_END_TIME));
      setConflictDialogOpen(true);
      return;
    }

    setAvailability(updatedAvailability);
  };

  const addTimeSlot = (day) => {
    const updatedAvailability = { ...availability };
    updatedAvailability[day].times.push({ start: null, end: null });
    setAvailability(updatedAvailability);
  };

  const removeTimeSlot = (day, index) => {
    if (availability[day].times.length === 1) {
      setRestrictionDialogOpen(true);
      return;
    }
    const updatedAvailability = { ...availability };
    updatedAvailability[day].times.splice(index, 1);
    setAvailability(updatedAvailability);
  };

  const handleRestrictionChange = (day, value) => {
    const updatedAvailability = { ...availability };
    updatedAvailability[day].restrictions = value;
    if (value === 'unavailable') {
      updatedAvailability[day].times = [];
    } else if (value === 'limited' && updatedAvailability[day].times.length === 0) {
      addTimeSlot(day);
    }
    setAvailability(updatedAvailability);
  };

  const validateTimes = (day, dayTimes) => {
    for (let i = 0; i < dayTimes.length; i++) {
      if (dayTimes[i].start && dayTimes[i].end && dayTimes[i].start.isAfter(dayTimes[i].end)) {
        setConflictMessage(t(StringKeys.START_TIME_CANNOT_BE_AFTER_END_TIME));
        setConflictDialogOpen(true);
        return false;
      }
      for (let j = i + 1; j < dayTimes.length; j++) {
        if (
          (dayjs(dayTimes[i].start).isBefore(dayjs(dayTimes[j].end)) && dayjs(dayTimes[i].end).isAfter(dayjs(dayTimes[j].start))) ||
          (dayjs(dayTimes[j].start).isBefore(dayjs(dayTimes[i].end)) && dayjs(dayTimes[j].end).isAfter(dayjs(dayTimes[i].start)))
        ) {
          return false;
        }
      }
    }
    return true;
  };

  const formatAvailability = (availability) => {
    return (
      <TableContainer>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Table aria-label="availability table">
            <TableBody>
              {weekDays.map(day => (
                <React.Fragment key={day}>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ width: '15%' }}>
                      {t(getDayTranslationKey(day))}
                    </TableCell>
                    <TableCell colSpan={isMobile ? 3 : 2}>
                      <FormControl fullWidth>
                        <InputLabel>{t(StringKeys.AVAILABILITY)}</InputLabel>
                        <Select
                          label={t(StringKeys.AVAILABILITY)}
                          value={availability[day].restrictions}
                          onChange={(e) => handleRestrictionChange(day, e.target.value)}
                        >
                          <MenuItem value="none">{t(StringKeys.AVAILABLE)}</MenuItem>
                          <MenuItem value="limited">{t(StringKeys.LIMITED)}</MenuItem>
                          <MenuItem value="unavailable">{t(StringKeys.UNAVAILABLE)}</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    {availability[day].restrictions === 'limited' && (
                      <TableCell sx={{ textAlign: 'left', padding: '0' }}>
                        <Button onClick={() => addTimeSlot(day)} startIcon={<AddIcon />}/>
                      </TableCell>
                    )}
                  </TableRow>
                  {availability[day].restrictions === 'limited' && availability[day].times.map((shift, index) => (
                    <TableRow key={`${day}-${index}`}>
                      <TableCell></TableCell>
                      <TableCell colSpan={2}>
                        <Grid container direction={isMobile ? "column" : "row"} alignItems={isMobile ? "center" : "start"}>
                          <Grid item>
                            <TimePicker
                              label={t(StringKeys.START)}
                              value={shift.start}
                              onChange={(newValue) => handleTimeChange(day, index, 'start', newValue)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={!!validationErrors[day]?.[index]}
                                  helperText={validationErrors[day]?.[index]}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item sx={{ mt: isMobile ? 2 : 0, ml: isMobile ? 0 : 2 }}>
                            <TimePicker
                              label={t(StringKeys.END)}
                              value={shift.end}
                              onChange={(newValue) => handleTimeChange(day, index, 'end', newValue)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={!!validationErrors[day]?.[index]}
                                  helperText={validationErrors[day]?.[index]}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell>
                        <Grid container justifyContent={isMobile ? "center" : "flex-start"}>
                          <Button onClick={() => removeTimeSlot(day, index)} startIcon={<DeleteIcon />}/>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </LocalizationProvider>
      </TableContainer>
    );
  };

  const formatAvailabilityMobile = (availability) => {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {weekDays.map(day => (
          <Grid container spacing={2} key={day} sx={{ mb: 3 }}>
            {/* Day Header */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t(getDayTranslationKey(day))}
              </Typography>
            </Grid>

            {/* Availability Dropdown */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t(StringKeys.AVAILABILITY)}</InputLabel>
                <Select
                  label={t(StringKeys.AVAILABILITY)}
                  value={availability[day].restrictions}
                  onChange={(e) => handleRestrictionChange(day, e.target.value)}
                >
                  <MenuItem value="none">{t(StringKeys.AVAILABLE)}</MenuItem>
                  <MenuItem value="limited">{t(StringKeys.LIMITED)}</MenuItem>
                  <MenuItem value="unavailable">{t(StringKeys.UNAVAILABLE)}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Time Slots */}
            {availability[day].restrictions === 'limited' && (
              <Grid item xs={12}>
                {availability[day].times.map((shift, index) => (
                  <Box key={`${day}-${index}`} sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      {t(StringKeys.AVAILABLE_TIME_SLOT)} {index + 1}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sx={{ mb: 2 }}>
                        <TimePicker
                          label={t(StringKeys.START)}
                          value={shift.start}
                          onChange={(newValue) => handleTimeChange(day, index, 'start', newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={!!validationErrors[day]?.[index]}
                              helperText={validationErrors[day]?.[index]}
                            />
                          )}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sx={{ mb: 2 }}>
                        <TimePicker
                          label={t(StringKeys.END)}
                          value={shift.end}
                          onChange={(newValue) => handleTimeChange(day, index, 'end', newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={!!validationErrors[day]?.[index]}
                              helperText={validationErrors[day]?.[index]}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button 
                          fullWidth
                          variant="contained"
                          color="error"
                          onClick={() => removeTimeSlot(day, index)}
                        >
                          {t(StringKeys.DELETE_TIME_SLOT)} {index + 1}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                ))}

                {/* Add Time Slot Button */}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => addTimeSlot(day)}
                  startIcon={<AddIcon />}
                  sx={{ mt: 1 }}
                >
                  {t(StringKeys.ADD_TIME_SLOT)}
                </Button>
              </Grid>
            )}
          </Grid>
        ))}
      </LocalizationProvider>
    );
  };

  if (!availability) return null;

  const actions = (
    <>
      <Button onClick={handleSubmit}>{t(StringKeys.SUBMIT)}</Button>
    </>
  );

  return (
    <BaseModal
      open={show}
      onClose={() => {
        resetForm();
        onHide();
      }}
      title={t(StringKeys.EDIT_AVAILABILITY)}
      actions={actions}
    >
      <Grid container spacing={2} margin="dense">
        {
          isMobile ? (
            <>
            {formatAvailabilityMobile(availability)} 
            </>
          ) 
          :
          (
            <>
            {formatAvailability(availability)}
            </>
          )
        }
      </Grid>
      <Dialog
        open={conflictDialogOpen}
        onClose={() => setConflictDialogOpen(false)}
      >
        <DialogTitle>{t(StringKeys.TIME_CONFLICT)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {conflictMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflictDialogOpen(false)} color="primary">
            {t(StringKeys.OK)}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={restrictionDialogOpen}
        onClose={() => setRestrictionDialogOpen(false)}
      >
        <DialogTitle>{t(StringKeys.LIMITED_AVAILABILITY)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(StringKeys.LIMITED_AVAILABILITY_REQUIRES_EMPLOYEE_AVAILABILITY_BE_SET_FOR_)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestrictionDialogOpen(false)} color="primary">
            {t(StringKeys.OK)}
          </Button>
        </DialogActions>
      </Dialog>
    </BaseModal>
  );
};

export default AvailabilityModal;